<template>
  <div id="orders-rewards-by-position">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title mb-1">
          {{ $t(`views.dashboardDetail.dividends.ordersRewardsByPosition.title`) }}
        </h1>
      </v-card-title>
      <v-card-text class="mt-0 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart v-if="loadedData" :chartData="barChartData" :chartOptions="barChartOptions" />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="orders-rewards-by-position-table"
            :headers="headers"
            :items="mySummary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="5"
            :footer-props="{
              'items-per-page-options': [5, 10, 15, 20, 30, 50, 100],
            }"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
            show-expand
            single-expand
          >
            <!-- <template slot="body.prepend">
              <tr v-if="totalRow" class="row-total">
                <td>TOTAL</td>
                <template v-if="type === 'scrip'">
                  <td class="text-right">
                    <currency :value="totalRow.positionNumber" />
                  </td>
                </template>
                <template v-else>
                  <td class="text-right">
                    <span />
                  </td>
                  <td class="text-right">
                    <currency :value="totalRow.totalBaseCurrency" :currency="user.baseCurrency" />
                  </td>
                  <td class="text-right">
                    <currency
                      :value="totalRow.totalWithComissionBaseCurrency"
                      :currency="user.baseCurrency"
                      strong
                    />
                  </td>
                </template>
              </tr>
            </template> -->
            <template v-slot:item.position.ticker="{ item }">
              <div class="items-flex">
                <TableTicker :position="item.position" />
                <template v-if="someNullExchangeRate(item)">
                  <div class="ml-3">
                    <WarningIcon
                      :text="$t(`views.dashboardDetail.dividends.ordersByPosition.warning`)"
                    />
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:item.total="{ item }">
              <currency
                v-if="item.position"
                :value="item.total"
                :currency="item.position.currency"
              />
              <span v-else />
            </template>
            <template v-slot:item.totalBaseCurrency="{ item }">
              <currency :value="item.totalBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.totalWithComissionBaseCurrency="{ item }">
              <currency
                :value="item.totalWithComissionBaseCurrency"
                :currency="user.baseCurrency"
                strong
              />
            </template>

            <template v-slot:item.data-table-expand="{ expand, isExpanded }">
              <v-icon size="25" class="ml-4" @click="expand(!isExpanded)">
                {{ !isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
              </v-icon>
            </template>

            <template v-slot:expanded-item="{ item }">
              <td :colspan="headers.length" class="pt-2 pb-3 px-1">
                <v-simple-table class="type-detail-table" dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th></th>
                        <th class="text-center">
                          {{ $t('views.orders.ordersTable.total') }}
                        </th>
                        <th class="text-center">
                          {{ $t('views.orders.ordersTable.total') }}
                          ({{ user.baseCurrency }})
                        </th>
                        <th class="text-center">
                          {{ $t('views.orders.ordersTable.totalWithComission') }}
                          ({{ user.baseCurrency }})
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(type, idx) in item.types" :key="idx">
                        <td>
                          {{
                            $t(
                              `views.dashboardDetail.dividends.ordersRewardsByPosition.${type.type}`
                            )
                          }}
                        </td>
                        <td class="text-center">
                          <currency
                            v-if="type.total"
                            :value="type.total"
                            :currency="item.position.currency"
                          />
                          <span v-else></span>
                        </td>
                        <td class="text-center">
                          <currency
                            v-if="type.totalBaseCurrency"
                            :value="type.totalBaseCurrency"
                            :currency="user.baseCurrency"
                          />
                          <span v-else></span>
                        </td>
                        <td class="text-center">
                          <currency
                            v-if="type.totalWithComissionBaseCurrency"
                            :value="type.totalWithComissionBaseCurrency"
                            :currency="user.baseCurrency"
                          />
                          <span v-else></span>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </td>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { map } from 'lodash'
import { mapState } from 'vuex'
import { getOrderSummary } from '@/lib/api.js'
import { ORDERS_TYPE_PALETTE, DEFAULT_PALETTE, DARK_DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency'
import BarChart from '@/views/components/core/chartjs/BarChart'
import BarLoading from '@/views/components/core/BarLoading'
import TableTicker from '@/views/components/common/TableTicker'
import WarningIcon from '@/views/components/common/WarningIcon.vue'

export default {
  components: { Currency, BarChart, BarLoading, TableTicker, WarningIcon },
  props: {
    showTable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    mySummary: [],
    // totalRow: null,
    currentPage: null,
    loadedData: false,
    barChartData: {
      labels: null,
      datasets: null,
    },
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this
      return {
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
              stacked: true,
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: context.user.baseCurrency,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
    headers() {
      return [
        {
          text: this.$t('views.orders.ordersTable.ticker'),
          value: 'position.ticker',
          divider: true,
        },
        {
          text: this.$t('views.orders.ordersTable.total'),
          value: 'total',
          align: 'right',
        },
        {
          text: `${this.$t('views.orders.ordersTable.total')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalWithComissionBaseCurrency',
          align: 'right',
        },

        {
          text: '',
          value: 'data-table-expand',
          sortable: false,
          align: 'right',
        },
      ]
    },
  },
  methods: {
    someNullExchangeRate(item) {
      return item.types.some(
        (type) => type.exchangeRates && type.exchangeRates.some((elem) => elem === null)
      )
    },
    itemRowBackground(item) {
      if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    getData(data, orderType) {
      return data.map((elem) => {
        if (!elem.types || !elem.types.length) return 0
        const bonus = elem.types.find((type) => type.type === orderType)
        if (!bonus) return 0
        return bonus.totalWithComissionBaseCurrency
      })
    },
    createBarChartObject(data) {
      this.barChartData.datasets = [
        {
          label: this.$t('views.dashboardDetail.dividends.ordersRewardsByPosition.stakeReward'),
          // this.$t('views.dashboardDetail.dividends.ordersRewardsByPosition.stakeReward') +
          // `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol( this.user.baseCurrency)})`,
          data: this.getData(data, 'stakeReward'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.chartsBarGreen
            : DEFAULT_PALETTE.chartsBarGreen,
        },
        {
          label: this.$t('views.dashboardDetail.dividends.ordersRewardsByPosition.bonus'),
          // this.$t('views.dashboardDetail.dividends.ordersRewardsByPosition.bonus') +
          // `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol( this.user.baseCurrency)})`,
          data: this.getData(data, 'bonus'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.chartsBarBlue
            : DEFAULT_PALETTE.chartsBarBlue,
        },
        {
          label: this.$t('views.dashboardDetail.dividends.ordersRewardsByPosition.drip'),
          // this.$t('views.dashboardDetail.dividends.ordersRewardsByPosition.drip') +
          // `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol( this.user.baseCurrency)})`,
          data: this.getData(data, 'drip'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.dark_backgroundSoftBlue
            : ORDERS_TYPE_PALETTE.drip,
        },
      ]
      this.barChartData.labels = map(data, (item) => {
        if (['fund'].indexOf(item.position.type) > -1 && item.position.name)
          return item.position.name
        return item.position.ticker
      })
    },
    async loadSummary(filters) {
      this.loadedData = false
      try {
        const result = await getOrderSummary({
          group: 'byPositionAndType',
          filter: {
            type: ['bonus', 'stakeReward', 'drip'],
            // positionType: { $ne: 'cash' },
            ...filters,
          },
        })
        const summary = result.data

        this.mySummary = summary.map((elem) => {
          const total = elem.types.shift()
          return {
            position: elem.position,
            ...total,
            types: elem.types,
          }
        })
        this.currentPage = 1

        this.createBarChartObject(summary)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
  },
}
</script>

<style lang="scss">
#orders-rewards-by-position {
  #orders-rewards-by-position-table {
    .items-flex {
      display: flex;
    }
  }
}
</style>
